import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { Company, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import User from "@mapmycustomers/shared/types/User";

import PreviewDealsListModal from "@app/component/PreviewDealsListModal";
import { showEntityView } from "@app/store/entityView/actions";

import useDealListColumns from "../useDealListColumns";

export interface Props {
  funnel?: Funnel;
  onHide: () => void;
  stage?: Stage;
  userIds: User["id"][];
}

const DealListModal: React.FC<Props & typeof mapDispatchToProps> = ({
  funnel,
  onHide,
  showEntityView,
  stage,
  userIds,
}) => {
  const intl = useIntl();
  const handleCompanyClick = useCallback(
    (entityId: Company["id"]) => showEntityView?.({ entityId, entityType: EntityType.COMPANY }),
    [showEntityView]
  );

  const handlePersonClick = useCallback(
    (entityId: Person["id"]) => showEntityView?.({ entityId, entityType: EntityType.PERSON }),
    [showEntityView]
  );
  const stageName = stage?.name;
  const columns = useDealListColumns(handleCompanyClick, handlePersonClick);

  const filter = useMemo(
    () => ({
      user: { operator: FilterOperator.IN_ANY, value: userIds },
      ...(stage
        ? { stage: { operator: FilterOperator.EQUALS, value: stage.id } }
        : funnel
        ? { funnel: { operator: FilterOperator.EQUALS, value: funnel.id } }
        : {}),
    }),
    [userIds, stage, funnel]
  );

  return (
    <PreviewDealsListModal
      columns={columns}
      filter={filter}
      onDealClick={(deal) => showEntityView({ entityId: deal.id, entityType: EntityType.DEAL })}
      onHide={onHide}
      stageName={stage?.name}
      subtitle={intl.formatMessage(
        {
          id: "reports.deals.progressionChart.stageName",
          defaultMessage: "{hasStageName, select, true {{stageName}} other {All stages}}",
          description: "Sub title of a deal list modal for Progression Chart in Reports",
        },
        { hasStageName: !!stageName, stageName }
      )}
      title={intl.formatMessage({
        id: "reports.deals.progressionChart.dealListModal",
        defaultMessage: "Deals Currently in Stage:",
        description: "Title of a deal list modal for Progression Chart in Reports",
      })}
    />
  );
};

const mapDispatchToProps = {
  showEntityView,
};

export default connect(null, mapDispatchToProps)(DealListModal);
